import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageSquareText } from "lucide-react";
import dustin from "@/assets/dustin.jpg";
import { goTo } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const lineAnim = (i) => ({
  initial: { y: "112%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, delay: 0.25 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero() {
  const { t } = useLang();
  const lines = t.hero.lines;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", "9%"]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen items-center overflow-hidden pb-16 pt-32 lg:pt-24">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1542259009477-d625272157b7?q=80&w=2200&auto=format&fit=crop"
          alt=""
          className="h-[115%] w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        <div className="hero-glow absolute inset-0" />
      </motion.div>

      <div className="mx-auto grid w-full max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1.12fr_0.88fr] lg:gap-10 lg:px-12">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-7 flex flex-wrap items-center gap-x-4 gap-y-2"
          >
            <span className="flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.26em] text-gold">
              <span className="h-1.5 w-1.5 rounded-full bg-gold" />
              {t.hero.badge}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-cream/50">{t.hero.location}</span>
          </motion.div>

          <h1 className="font-display text-[clamp(3.4rem,10vw,8.5rem)] font-semibold leading-[0.92] tracking-tight text-cream">
            {lines.map((text, i) => (
              <span key={text} className="block overflow-hidden pb-1">
                <motion.span
                  style={{ display: "block" }}
                  {...lineAnim(i)}
                  className={i === lines.length - 1 ? "text-gold-grad italic pr-2" : "pr-2"}
                >
                  {text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg"
          >
            {t.hero.paragraph}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#consult"
              onClick={(e) => goTo(e, "#consult")}
              data-testid="hero-book-cta"
              className="btn-gold group inline-flex items-center gap-2.5 rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em]"
            >
              {t.hero.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href={`sms:+18082984096?&body=${encodeURIComponent(t.consult.smsBody)}`}
              data-testid="hero-text-cta"
              className="inline-flex items-center gap-2.5 rounded-full border border-cream/25 px-7 py-4 text-sm font-medium uppercase tracking-[0.14em] text-cream transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
            >
              <MessageSquareText className="h-4 w-4 text-gold" />
              {t.hero.textCta}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[10px] uppercase tracking-[0.24em] text-cream/55"
          >
            {t.hero.chips.map((chip) => (
              <span key={chip} className="flex items-center gap-2">
                <span className="h-1 w-1 rotate-45 bg-gold" />
                {chip}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: photoY }} className="relative mx-auto w-full max-w-md lg:max-w-none">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-b-3xl rounded-t-[240px] border border-gold/25 sm:-inset-4" aria-hidden="true" />
            <div
              className="overflow-hidden rounded-b-2xl rounded-t-[220px] border border-gold/40 shadow-[0_40px_90px_rgba(0,0,0,0.5)]"
              data-testid="hero-portrait"
            >
              <img
                src={dustin}
                alt={t.hero.imgAlt}
                className="h-[420px] w-full object-cover object-top sm:h-[540px]"
              />
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-4 bottom-10 rounded-2xl border border-gold/25 bg-navy2/90 px-5 py-4 shadow-2xl backdrop-blur-md sm:-left-10"
              data-testid="hero-chip-legacy"
            >
              <p className="font-display text-lg italic text-gold-light">{t.hero.floatA}</p>
              <p className="font-display text-lg italic text-gold-light">{t.hero.floatB}</p>
            </motion.div>
            <motion.div
              animate={{ y: [0, 9, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -right-3 top-16 rounded-full border border-gold/25 bg-ink/85 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold backdrop-blur-md sm:-right-6"
            >
              {t.hero.floatBadge}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
